import { useCallback, useMemo, useState } from "react";
import type {
	EmailToTaskEditable,
	EmailToTaskPatch,
	EmailToTaskSettings,
	SecretField,
} from "@/services/email-to-task.service";

const EDITABLE_KEYS: (keyof EmailToTaskEditable)[] = [
	"enabled",
	"inbound_address",
	"hub_base_url",
	"hub_agent_id",
	"hub_agent_name",
	"extra_instructions",
	"request_timeout_sec",
	"allowed_sender_domains",
	"allowed_sender_emails",
	"require_taskhub_user",
	"require_dkim_pass",
	"default_project_id",
	"default_priority",
	"assign_active_sprint",
	"grouping_mode",
	"nested_list_mode",
	"resolve_due_dates",
	"review_mode",
	"notify_sender_on_ready",
	"notify_sender_on_approved",
	"notify_reviewers_in_app",
	"max_attempts",
];

/** Per secret: undefined = unchanged, "" typed-empty = unchanged, null = clear. */
export type SecretDrafts = Partial<Record<SecretField, string | null>>;

function pickEditable(settings: EmailToTaskSettings): EmailToTaskEditable {
	const out = {} as Record<string, unknown>;
	for (const key of EDITABLE_KEYS) out[key] = settings[key];
	return out as EmailToTaskEditable;
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function useEmailToTaskForm(settings: EmailToTaskSettings | undefined) {
	const [form, setForm] = useState<EmailToTaskEditable | null>(null);
	const [secrets, setSecrets] = useState<SecretDrafts>({});

	const reset = useCallback((next: EmailToTaskSettings) => {
		setForm(pickEditable(next));
		setSecrets({});
	}, []);

	// First load: seed the form during render (no effect round-trip).
	if (settings && !form) {
		setForm(pickEditable(settings));
	}

	const setField = useCallback(
		<K extends keyof EmailToTaskEditable>(key: K, value: EmailToTaskEditable[K]) => {
			setForm((f) => (f ? { ...f, [key]: value } : f));
		},
		[],
	);

	const setSecret = useCallback((key: SecretField, value: string | null) => {
		setSecrets((s) => ({ ...s, [key]: value }));
	}, []);

	const patch = useMemo<EmailToTaskPatch>(() => {
		if (!settings || !form) return {};
		const diff: Record<string, unknown> = {};
		for (const key of EDITABLE_KEYS) {
			if (!sameValue(form[key], settings[key])) diff[key] = form[key];
		}
		for (const [key, value] of Object.entries(secrets)) {
			if (value === null) diff[key] = null;
			else if (value && value.trim()) diff[key] = value.trim();
		}
		return diff as EmailToTaskPatch;
	}, [settings, form, secrets]);

	return {
		form,
		secrets,
		setField,
		setSecret,
		patch,
		dirty: Object.keys(patch).length > 0,
		reset,
	};
}

export type EmailToTaskFormApi = ReturnType<typeof useEmailToTaskForm> & {
	form: EmailToTaskEditable;
	settings: EmailToTaskSettings;
	disabled: boolean;
};
